import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { EmailVerificationProvider } from './contexts/EmailVerificationContext';
import { UserProfileProvider } from './contexts/UserProfileContext';
import { DarkModeProvider } from './contexts/DarkModeContext';
import { ProgressProvider } from './contexts/ProgressContext';
import ErrorBoundary from './components/ErrorBoundary';
import EmailVerificationGuard from './components/EmailVerificationGuard';
import PhoneVerificationGuard from './components/PhoneVerificationGuard';
import ProfileCompletionGuard from './components/ProfileCompletionGuard';
import VerifyEmail from './components/VerifyEmail';
import VerifyPhone from './components/VerifyPhone';
import PracticeArenaPage from './components/PracticeArenaPage';
import DailyChallengeArena from './components/DailyChallengeArena';
import PaymentPage from './components/payment/PaymentPage';
import PaymentSuccessPage from './components/payment/PaymentSuccessPage';
import PaymentFailurePage from './components/payment/PaymentFailurePage';

// Auth Components (immediate load)
import SignUp from './components/SignUp';
import SignIn from './components/SignIn';
import NewLandingPage from './components/NewLandingPage';
import BlogsPage from './components/BlogsPage';
import BlogDetailPage from './components/BlogDetailPage';
import MyBlogsPage from './components/MyBlogsPage';
import WriteBlogPage from './components/WriteBlogPage';

// Core Components (immediate load for better UX)
import Dashboard from './components/Dashboard';
import Comparator from './components/Comparator';
import CollegeProfile from './components/CollegeProfile';

// Heavy/Rarely Used Components (lazy load)
const TutorialsPage = React.lazy(() => import('./components/TutorialsPage'));
const CodeEditor = React.lazy(() => import('./components/CodeEditor'));
const AdminLogin = React.lazy(() => import('./components/AdminLogin'));
const AdminDashboard = React.lazy(() => import('./components/AdminDashboard'));
const AdminBlogReviewPage = React.lazy(() => import('./components/AdminBlogReviewPage'));
const AdminPaymentHistoryPage = React.lazy(() => import('./components/AdminPaymentHistoryPage'));

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }
  
  return isAuthenticated ? children : <Navigate to="/signin" replace />;
};

const ProtectedAdminRoute = ({ children }) => {
  const adminToken = sessionStorage.getItem('adminToken');
  const isAdmin = sessionStorage.getItem('isAdmin') === 'true';
  return isAdmin && adminToken ? children : <Navigate to="/admin_login" replace />;
};

const PublicAuthRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return isAuthenticated ? <Navigate to="/dashboard" replace /> : children;
};

const HomeRedirect = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return <Navigate to={isAuthenticated ? '/dashboard' : '/'} replace />;
};

// Loading component for Suspense
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
  </div>
);

const CRITICAL_IMAGE_ASSETS = [
  '/landing-bg.jpeg',
  '/registraion-bg.png',
  '/admin-login-bg.jpeg',
  '/favicon.png',
  '/default-images/campus/default_campus.png',
  '/default-images/Logo/default_logo.png'
];

function App() {
  // Preload heavy components after initial load
  React.useEffect(() => {
    const preloadedImages = [];

    CRITICAL_IMAGE_ASSETS.forEach((src) => {
      const image = new Image();
      image.decoding = 'async';
      image.src = src;
      preloadedImages.push(image);
    });

    const preloadHeavyComponents = () => {
      // Preload after 2 seconds of idle time
      setTimeout(() => {
        if (window.requestIdleCallback) {
          requestIdleCallback(() => {
            import('./components/CodeEditor');
            import('./components/TutorialsPage');
          });
        } else {
          // Fallback for browsers without requestIdleCallback
          setTimeout(() => {
            import('./components/CodeEditor');
            import('./components/TutorialsPage');
          }, 100);
        }
      }, 2000);
    };
    
    // Preload AdminDashboard if admin is logged in
    const preloadAdminComponents = () => {
      const adminToken = sessionStorage.getItem('adminToken');
      const isAdmin = sessionStorage.getItem('isAdmin') === 'true';
      
      if (adminToken && isAdmin) {
        // Immediately preload AdminDashboard for logged-in admin
        import('./components/AdminDashboard');
        import('./components/AdminBlogReviewPage');
        import('./components/AdminPaymentHistoryPage');
      }
    };
    
    preloadHeavyComponents();
    preloadAdminComponents();
    
    // Listen for admin login events to preload AdminDashboard
    const handleStorageChange = (e) => {
      if (e.key === 'adminToken' || e.key === 'isAdmin') {
        preloadAdminComponents();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  return (
    <ErrorBoundary>
      <DarkModeProvider>
        <ProgressProvider>
          <AuthProvider>
          <EmailVerificationProvider>
            <UserProfileProvider>
              <Router>
                <div className="min-h-screen bg-gray-100 dark:bg-gray-900 transition-colors">
                  <Routes>
                  {/* Entry Routes */}
                  <Route path="/" element={<NewLandingPage />} />
                  <Route path="/blogs" element={<BlogsPage />} />
                  <Route path="/blogs/write" element={<ProtectedRoute><WriteBlogPage /></ProtectedRoute>} />
                  <Route path="/my-blogs" element={<ProtectedRoute><MyBlogsPage /></ProtectedRoute>} />
                  <Route path="/blogs/:slug" element={<BlogDetailPage />} />
                  <Route path="/practice-arena" element={<PracticeArenaPage />} />
                  <Route path="/practice-arena/:section" element={<PracticeArenaPage />} />
                  <Route path="/daily-challenge" element={<DailyChallengeArena />} />
                  <Route path="/payment" element={<PaymentPage />} />
                  <Route path="/payment/success" element={<PaymentSuccessPage />} />
                  <Route path="/payment/failure" element={<PaymentFailurePage />} />
                  <Route path="/signup" element={<PublicAuthRoute><SignUp /></PublicAuthRoute>} />
                  <Route path="/signin" element={<PublicAuthRoute><SignIn /></PublicAuthRoute>} />
                  <Route path="/verify-email" element={<ProtectedRoute><VerifyEmail /></ProtectedRoute>} />
                  <Route path="/verify-phone" element={<ProtectedRoute><EmailVerificationGuard><VerifyPhone /></EmailVerificationGuard></ProtectedRoute>} />
                  
                  {/* Authenticated User Routes */}
                  <Route path="/dashboard" element={withUserGuards(<Dashboard />)} />
                  <Route path="/comparator" element={withUserGuards(<Comparator />)} />
                  <Route path="/college-profile" element={withUserGuards(<CollegeProfile />)} />
                  <Route path="/college-profile/:slug" element={withUserGuards(<CollegeProfile />)} />
                  <Route path="/tutorials" element={withUserGuards(<Suspense fallback={<PageLoader />}><TutorialsPage /></Suspense>)} />
                  <Route path="/tutorials/:domainSlug" element={withUserGuards(<Suspense fallback={<PageLoader />}><TutorialsPage /></Suspense>)} />
                  <Route path="/tutorials/:domainSlug/:subjectSlug" element={withUserGuards(<Suspense fallback={<PageLoader />}><TutorialsPage /></Suspense>)} />
                  <Route path="/tutorials/:domainSlug/:subjectSlug/:topicSlug" element={withUserGuards(<Suspense fallback={<PageLoader />}><TutorialsPage /></Suspense>)} />
                  <Route path="/compiler" element={withUserGuards(<Suspense fallback={<PageLoader />}><CodeEditor /></Suspense>)} />
                  
                  {/* Admin Routes */}
                  <Route path="/admin_login" element={<Suspense fallback={<PageLoader />}><AdminLogin /></Suspense>} />
                  <Route path="/admin_dashboard" element={<ProtectedAdminRoute><Suspense fallback={<PageLoader />}><AdminDashboard /></Suspense></ProtectedAdminRoute>} />
                  <Route path="/admin_blog_reviews" element={<ProtectedAdminRoute><Suspense fallback={<PageLoader />}><AdminBlogReviewPage /></Suspense></ProtectedAdminRoute>} />
                  <Route path="/admin_payment_history" element={<ProtectedAdminRoute><Suspense fallback={<PageLoader />}><AdminPaymentHistoryPage /></Suspense></ProtectedAdminRoute>} />
                  <Route path="*" element={<HomeRedirect />} />
                </Routes>
              </div>
            </Router>
          </UserProfileProvider>
        </EmailVerificationProvider>
          </AuthProvider>
        </ProgressProvider>
      </DarkModeProvider>
    </ErrorBoundary>
  );
}

const withUserGuards = (element) => (
  <ProtectedRoute>
    <EmailVerificationGuard>
      <ProfileCompletionGuard>
        <PhoneVerificationGuard>
          {element}
        </PhoneVerificationGuard>
      </ProfileCompletionGuard>
    </EmailVerificationGuard>
  </ProtectedRoute>
);

export default App;
